import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { AuthMe } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageHeader } from "@/modules/shell/PageHeader";
import { fetchMe, logout } from "@/shared/api/auth";

export function SelectCompanyPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState<AuthMe | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((data) => {
        if (cancelled) return;
        // Superadmins always see the picker so Platform stays reachable
        // without putting god-mode links inside company UI (plan/18).
        if (data.companies.length === 1 && !data.user.isSuperadmin) {
          const slug = data.companies.map((c) => c.slug)[0];
          if (slug) {
            navigate(`/c/${slug}`, { replace: true });
            return;
          }
        }
        setMe(data);
      })
      .catch(() => {
        if (!cancelled) navigate("/login", { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function onLogout() {
    try {
      await logout();
    } catch {
      /* ignore */
    }
    navigate("/login", { replace: true });
  }

  if (!me) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 animate-[fadeIn_240ms_ease-out]">
        <Alert variant="muted">Loading companies…</Alert>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 py-12 animate-[softRise_400ms_ease-out]">
      <PageHeader
        title="Choose company"
        description={`Signed in as ${me.user.email ?? me.user.displayName}`}
      />

      {me.companies.length === 0 ? (
        <div className="mt-8">
          <EmptyState
            title="No company memberships yet"
            description={
              me.user.isSuperadmin
                ? "Create a company in Platform to get started."
                : "Ask an owner to invite you."
            }
            action={
              me.user.isSuperadmin ? (
                <Button asChild size="sm">
                  <Link to="/superadmin" className="no-underline">
                    Open Platform
                  </Link>
                </Button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <ul className="mt-8 m-0 list-none space-y-2 p-0">
          {me.companies.map((co) => (
            <li key={co.companyId}>
              <Button asChild className="w-full justify-start" variant="outline">
                <Link to={`/c/${co.slug}`} className="no-underline">
                  {co.name}
                </Link>
              </Button>
            </li>
          ))}
        </ul>
      )}

      {me.user.isSuperadmin && (
        <Button asChild className="mt-6 w-full" variant="outline">
          <Link to="/superadmin" className="no-underline">
            Platform
          </Link>
        </Button>
      )}

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}

      <button
        type="button"
        className="mt-10 border-0 bg-transparent p-0 text-sm text-[var(--color-muted)] underline-offset-2 hover:underline"
        onClick={() => {
          setError(null);
          void onLogout();
        }}
      >
        Sign out
      </button>
    </div>
  );
}
